import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import DischargeSummaryForm, {
    parseMultiSelect,
    DISCHARGE_TYPE_OPTIONS,
    SERVICES_OPTIONS,
    PROGRESS_OPTIONS,
    CONDITION_OPTIONS,
    IRP_DOMAIN_OPTIONS,
    INSTRUCTIONS_OPTIONS,
    FAMILY_COUNSELING_OPTIONS,
    EDUCATION_OPTIONS,
    TRANSITION_OPTIONS
} from "./DischargeSummaryForm";
import { getApiErrorMessage } from "../../../utils/apiError";
import {
    fetchDischargeSummary,
    updateDischargeSummary,
    clearError,
    clearCurrentDischargeSummary
} from "../../../store/slices/dischargeSummarySlice";

const EditDischargeSummary = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId, type_id } = state || {}; // ✅ Get type_id from navigation state

    // Redux state
    const {
        currentDischargeSummary,
        isLoading,
        error
    } = useSelector((state) => state.dischargeSummary);

    const [formData, setFormData] = useState({
        // Client Information
        client_name: "",
        CNIC: "",
        gender: "",
        date_of_birth: "",
        program: "",
        admission_date: "",
        discharge_date: "",

        // Provisional Psychiatric Diagnosis
        primary_diagnosis: "",
        secondary_diagnosis: "",
        co_morbidities: "",
        presenting_symptoms: "",

        // Discharge Planning
        discharge_types: [],

        // Services Provided
        service_provided: [],

        // Clinical Reports (text)
        psychiatrist_remarks: "",
        care_manager_remarks: "",
        primary_care_remarks: "",

        // Progress since admission
        clinical_progress: [],
        mental_state_at_discharge: [],
        complications: "",

        // Individualized Rehabilitation Plan, Progress & Outcomes
        irp_domains: [],
        short_term_goals: "",
        long_term_goals: "",
        current_progress: [],
        progress_achieved_text: "",
        progress_in_progress_text: "",
        progress_not_achieved_text: "",
        additional_responsibilities: "",
        outcomes: "",

        // Medications
        discharge_medications: [
            { name: "", dosage: "", frequency: "", route: "" }
        ],

        // Instructions / Family Counseling
        instructions: [],
        family_counseling: [],

        // Home Care & Future Support Plan
        teaching_provided_to: [],
        recovery_goals: "",
        possible_relapse_signs: "",
        injections: "",
        risk_assessment: "",
        who_to_contact: "",
        where_to_go_crisis: "",
        follow_up_psychiatrist: "",
        physical_health_needs: "",
        special_instructions: "",
        referred_for: [],
        referred_for_other_text: "",
        next_of_kin_informed: "",
        emergency_contact_provided: "",
        safety_care_plan: "",

        // Final Sections
        trh_recommendations: "",

        // Signatures (name + uploaded image)
        consultant_psychiatrist_name: "",
        prp_name: "",
        guardian_name: "",
        client_sign_name: "",

        type_id: type_id || null,
    });

    // State for file inputs and existing file URLs
    const [fileData, setFileData] = useState({
        primary_care_attachment: { file: null, existingUrl: null, existingPath: null },
        psychiatrist_attachment: { file: null, existingUrl: null, existingPath: null },
        care_manager_attachment: { file: null, existingUrl: null, existingPath: null },
        consultant_psychiatrist_signature: { file: null, existingUrl: null, existingPath: null },
        prp_signature: { file: null, existingUrl: null, existingPath: null },
        guardian_signature: { file: null, existingUrl: null, existingPath: null },
        client_signature: { file: null, existingUrl: null, existingPath: null },
    });

    // Fetch discharge summary data on component mount
    useEffect(() => {
        if (id) {
            dispatch(fetchDischargeSummary(id));
        }
    }, [id, dispatch]);

    // Populate form when data is fetched
    useEffect(() => {
        if (currentDischargeSummary) {
            console.log("Current discharge summary:", currentDischargeSummary);

            const transformedData = {
                // Foreign Keys
                patient_id: currentDischargeSummary.patient_id,
                case_id: currentDischargeSummary.case_id,
                program_master_id: currentDischargeSummary.program_master_id,
                type_id: currentDischargeSummary.type_id || type_id,

                // Client Information
                client_name: currentDischargeSummary.client_name || "",
                CNIC: currentDischargeSummary.CNIC || "",
                gender: currentDischargeSummary.gender || "",
                date_of_birth: currentDischargeSummary.date_of_birth ? currentDischargeSummary.date_of_birth.split('T')[0] : "",
                program: currentDischargeSummary.program || "",
                admission_date: currentDischargeSummary.admission_date ? currentDischargeSummary.admission_date.split('T')[0] : "",
                discharge_date: currentDischargeSummary.discharge_date ? currentDischargeSummary.discharge_date.split('T')[0] : "",

                // Provisional Psychiatric Diagnosis
                primary_diagnosis: currentDischargeSummary.primary_diagnosis || "",
                secondary_diagnosis: currentDischargeSummary.secondary_diagnosis || "",
                co_morbidities: currentDischargeSummary.co_morbidities || "",
                presenting_symptoms: currentDischargeSummary.presenting_symptoms || "",

                // Discharge Planning
                discharge_types: parseMultiSelect(currentDischargeSummary.discharge_types, DISCHARGE_TYPE_OPTIONS),

                // Services Provided
                service_provided: parseMultiSelect(currentDischargeSummary.service_provided, SERVICES_OPTIONS),

                // Clinical Reports (text)
                psychiatrist_remarks: currentDischargeSummary.psychiatrist_remarks || "",
                care_manager_remarks: currentDischargeSummary.care_manager_remarks || "",
                primary_care_remarks: currentDischargeSummary.primary_care_remarks || "",

                // Progress since admission
                clinical_progress: parseMultiSelect(currentDischargeSummary.clinical_progress, PROGRESS_OPTIONS),
                mental_state_at_discharge: parseMultiSelect(currentDischargeSummary.mental_state_at_discharge, CONDITION_OPTIONS),
                complications: currentDischargeSummary.complications || "",

                // Individualized Rehabilitation Plan, Progress & Outcomes
                irp_domains: parseMultiSelect(currentDischargeSummary.irp_domains, IRP_DOMAIN_OPTIONS),
                short_term_goals: currentDischargeSummary.short_term_goals || "",
                long_term_goals: currentDischargeSummary.long_term_goals || "",
                current_progress: parseMultiSelect(currentDischargeSummary.current_progress, ["Achieved", "In Progress", "Not Achieved"]),
                progress_achieved_text: currentDischargeSummary.progress_achieved_text || "",
                progress_in_progress_text: currentDischargeSummary.progress_in_progress_text || "",
                progress_not_achieved_text: currentDischargeSummary.progress_not_achieved_text || "",
                additional_responsibilities: currentDischargeSummary.additional_responsibilities || "",
                outcomes: currentDischargeSummary.outcomes || "",

                // Medications
                discharge_medications: currentDischargeSummary.medications?.map(med => ({
                    id: med.id,
                    name: med.name || "",
                    dosage: med.dosage || "",
                    frequency: med.frequency || "",
                    route: med.route || ""
                })) || [{ name: "", dosage: "", frequency: "", route: "" }],

                // Instructions / Family Counseling
                instructions: parseMultiSelect(currentDischargeSummary.instructions, INSTRUCTIONS_OPTIONS),
                family_counseling: parseMultiSelect(currentDischargeSummary.family_counseling, FAMILY_COUNSELING_OPTIONS),

                // Home Care & Future Support Plan
                teaching_provided_to: parseMultiSelect(currentDischargeSummary.teaching_provided_to, EDUCATION_OPTIONS),
                recovery_goals: currentDischargeSummary.recovery_goals || "",
                possible_relapse_signs: currentDischargeSummary.possible_relapse_signs || "",
                injections: currentDischargeSummary.injections || "",
                risk_assessment: currentDischargeSummary.risk_assessment || "",
                who_to_contact: currentDischargeSummary.who_to_contact || "",
                where_to_go_crisis: currentDischargeSummary.where_to_go_crisis || "",
                follow_up_psychiatrist: currentDischargeSummary.follow_up_psychiatrist ? currentDischargeSummary.follow_up_psychiatrist.split('T')[0] : "",
                physical_health_needs: currentDischargeSummary.physical_health_needs || "",
                special_instructions: currentDischargeSummary.special_instructions || "",
                referred_for: parseMultiSelect(currentDischargeSummary.referred_for, TRANSITION_OPTIONS),
                referred_for_other_text: currentDischargeSummary.referred_for_other_text || "",
                next_of_kin_informed: currentDischargeSummary.next_of_kin_informed || "",
                emergency_contact_provided: currentDischargeSummary.emergency_contact_provided || "",
                safety_care_plan: currentDischargeSummary.safety_care_plan || "",

                // Final Sections
                trh_recommendations: currentDischargeSummary.trh_recommendations || "",

                // Signatures (names — images handled via fileData)
                consultant_psychiatrist_name: currentDischargeSummary.consultant_psychiatrist_name || "",
                prp_name: currentDischargeSummary.prp_name || "",
                guardian_name: currentDischargeSummary.guardian_name || "",
                client_sign_name: currentDischargeSummary.client_sign_name || "",
            };

            // Set file data with URLs
            const baseUrl = "https://trh-backend.clouxiplexi.com/storage/";
            setFileData({
                primary_care_attachment: {
                    file: null,
                    existingUrl: currentDischargeSummary.primary_care_attachment ? `${baseUrl}${currentDischargeSummary.primary_care_attachment}` : null,
                    existingPath: currentDischargeSummary.primary_care_attachment
                },
                psychiatrist_attachment: {
                    file: null,
                    existingUrl: currentDischargeSummary.psychiatrist_attachment ? `${baseUrl}${currentDischargeSummary.psychiatrist_attachment}` : null,
                    existingPath: currentDischargeSummary.psychiatrist_attachment
                },
                care_manager_attachment: {
                    file: null,
                    existingUrl: currentDischargeSummary.care_manager_attachment ? `${baseUrl}${currentDischargeSummary.care_manager_attachment}` : null,
                    existingPath: currentDischargeSummary.care_manager_attachment
                },
                consultant_psychiatrist_signature: {
                    file: null,
                    existingUrl: currentDischargeSummary.consultant_psychiatrist_signature ? `${baseUrl}${currentDischargeSummary.consultant_psychiatrist_signature}` : null,
                    existingPath: currentDischargeSummary.consultant_psychiatrist_signature
                },
                prp_signature: {
                    file: null,
                    existingUrl: currentDischargeSummary.prp_signature ? `${baseUrl}${currentDischargeSummary.prp_signature}` : null,
                    existingPath: currentDischargeSummary.prp_signature
                },
                guardian_signature: {
                    file: null,
                    existingUrl: currentDischargeSummary.guardian_signature ? `${baseUrl}${currentDischargeSummary.guardian_signature}` : null,
                    existingPath: currentDischargeSummary.guardian_signature
                },
                client_signature: {
                    file: null,
                    existingUrl: currentDischargeSummary.client_signature ? `${baseUrl}${currentDischargeSummary.client_signature}` : null,
                    existingPath: currentDischargeSummary.client_signature
                },
            });

            console.log("Transformed data for form:", transformedData);
            setFormData(transformedData);
        }
    }, [currentDischargeSummary, type_id]);

    // Clear errors and current data on unmount
    useEffect(() => {
        return () => {
            dispatch(clearError());
            dispatch(clearCurrentDischargeSummary());
        };
    }, [dispatch]);

    // Handle form changes from child component
    const handleChange = (e) => {
        const { name, value, type, checked, files } = e.target;

        // 1️⃣ FILE INPUTS
        if (type === "file") {
            const file = files[0] || null;
            setFileData((prev) => ({
                ...prev,
                [name]: { ...prev[name], file }
            }));
            return;
        }

        // 2️⃣ MULTI-CHECKBOX FIELDS (arrays)
        const multiSelectFields = [
            "discharge_types",
            "teaching_provided_to",
            "service_provided",
            "clinical_progress",
            "mental_state_at_discharge",
            "irp_domains",
            "current_progress",
            "instructions",
            "family_counseling",
            "referred_for"
        ];

        if (multiSelectFields.includes(name)) {
            setFormData((prev) => ({
                ...prev,
                [name]: checked
                    ? [...prev[name], value]
                    : prev[name].filter(v => v !== value)
            }));
            return;
        }

        // 3️⃣ BOOLEAN CHECKBOXES
        if (type === "checkbox") {
            setFormData((prev) => ({
                ...prev,
                [name]: checked
            }));
            return;
        }

        // 4️⃣ DEFAULT HANDLER
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Handle medications array updates from child component
    const handleMedicationsChange = (medications) => {
        setFormData((prev) => ({
            ...prev,
            discharge_medications: medications,
        }));
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Create FormData object for file uploads
        const submitData = new FormData();

        // Append required IDs explicitly
        submitData.append('case_id', caseId);
        submitData.append('patient_id', patientId);
        if (formData.type_id) {
            submitData.append('type_id', formData.type_id); // ✅ NEW: Include type_id
        }

        // Filter out empty medications
        const validMedications = formData.discharge_medications.filter(med =>
            med.name && med.name.trim() !== ""
        );

        console.log("Valid medications to send:", validMedications);
        console.log("File data:", fileData);

        // Append all form fields to FormData
        Object.keys(formData).forEach(key => {
            if (key === 'discharge_medications') {
                if (validMedications.length > 0) {
                    validMedications.forEach((medication, index) => {
                        if (medication.id && Number.isInteger(medication.id)) {
                            submitData.append(`discharge_medications[${index}][id]`, medication.id);
                        }
                        submitData.append(`discharge_medications[${index}][name]`, medication.name || '');
                        submitData.append(`discharge_medications[${index}][dosage]`, medication.dosage || '');
                        submitData.append(`discharge_medications[${index}][frequency]`, medication.frequency || '');
                        submitData.append(`discharge_medications[${index}][route]`, medication.route || '');
                    });
                } else {
                    submitData.append(`discharge_medications[0][name]`, '');
                    submitData.append(`discharge_medications[0][dosage]`, '');
                    submitData.append(`discharge_medications[0][frequency]`, '');
                    submitData.append(`discharge_medications[0][route]`, '');
                }
            }
            else if (!key.includes('_attachment') && formData[key] !== null && formData[key] !== undefined && formData[key] !== "") {
                submitData.append(key, formData[key]);
            }
        });

        // Handle file uploads separately (report attachments + signature images)
        const fileFields = [
            'primary_care_attachment', 'psychiatrist_attachment', 'care_manager_attachment',
            'consultant_psychiatrist_signature', 'prp_signature', 'guardian_signature', 'client_signature'
        ];
        fileFields.forEach(field => {
            if (fileData[field] && fileData[field].file) {
                submitData.append(field, fileData[field].file);
            }
        });

        // Add _method for PUT request
        submitData.append('_method', 'PUT');

        // Debug: Log FormData contents
        console.log("FormData contents for update:");
        for (let pair of submitData.entries()) {
            console.log(pair[0] + ': ', pair[1]);
        }

        try {
            const response = await dispatch(updateDischargeSummary({
                case_id: caseId,
                formData: submitData
            })).unwrap();

            Swal.fire({
                title: "Success!",
                text: response.message || "Discharge Summary updated successfully.", // Backend se message show karein
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });

            // Stay on edit page
        } catch (error) {
            console.error("Update error:", error);

            const errorMessage = getApiErrorMessage(error, "Failed to update Discharge Summary");

            Swal.fire({
                title: "Error!",
                text: errorMessage,
                icon: "error",
                confirmButtonText: "Try Again",
            });
        }
    };

    // Loading state
    if (isLoading && !formData.client_name) {
        return (
            <MainLayout>
                <div className="container-xxl flex-grow-1 container-p-y">
                    <div className="card">
                        <div className="card-body text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3 text-muted">Loading discharge summary...</p>
                        </div>
                    </div>
                </div>
            </MainLayout>
        );
    }

    // Error state
    if (error && !formData.client_name) {
        return (
            <MainLayout>
                <div className="container-xxl flex-grow-1 container-p-y">
                    <div className="card">
                        <div className="card-body text-center py-5">
                            <i className="ri-error-warning-line fs-1 text-danger d-block mb-3"></i>
                            <h5 className="text-danger">Error Loading Data</h5>
                            <p className="text-muted">{error}</p>
                            <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => dispatch(fetchDischargeSummary(id))}
                            >
                                Retry
                            </button>
                            <button
                                className="btn btn-outline-secondary btn-sm ms-2"
                                onClick={() => navigate(`/cases/${caseId}/profile`, {
                                    state: { patientId, caseId }
                                })}
                            >
                                Back to Case
                            </button>
                        </div>
                    </div>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Breadcrumbs />
                    <Link
                        to={`/phase07/discharge-summary/print/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        <i className="ri-printer-line me-1"></i> Print
                    </Link>
                </div>
                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <DischargeSummaryForm
                                formData={formData}
                                handleChange={handleChange}
                                handleMedicationsChange={handleMedicationsChange}
                                isSubmitting={isLoading}
                                isEdit={true}
                                fileData={fileData}
                                typeId={formData.type_id} // ✅ Pass type_id to form
                            />
                            <div className="row mt-4">
                                <div className="col-md-2 ms-auto">
                                    <button
                                        type="submit"
                                        className="btn btn-primary w-100 sticky-form-submit"
                                        disabled={isLoading}
                                    >
                                        <i className="ri-save-3-line me-1"></i>
                                        {isLoading ? "Updating..." : "Update"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditDischargeSummary;